import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { supabase } from '../supabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = async (accessToken) => {
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setToken(accessToken);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setUser(null);
      setToken(null);
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.access_token) {
        fetchUserProfile(session.access_token).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.access_token) {
        await fetchUserProfile(session.access_token);
      } else {
        setUser(null);
        setToken(null);
        delete axios.defaults.headers.common['Authorization'];
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const sendMagicLink = async (email) => {
    const redirectTo = window.location.origin;
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
          emailRedirectTo: redirectTo,
        }
      });
      if (error) throw error;
    } catch (error) {
      // Supabase-JS v2.98 has a quirk where a 429 rate-limit response
      // sometimes bubbles up as "Failed to execute 'json' on 'Response':
      // body stream already read". Translate it into a human message.
      const msg = (error && error.message) || '';
      const status = error?.status || error?.statusCode;
      if (status === 429 || /rate limit|too many/i.test(msg)) {
        const e = new Error("You've requested a few links in a row. Please wait about a minute, then try again.");
        e.status = 429;
        throw e;
      }
      if (/body stream already read/i.test(msg)) {
        const e = new Error("We couldn't send the link right now — this usually means too many requests in a short time. Wait about a minute and try again.");
        e.status = 429;
        throw e;
      }
      throw error;
    }
  };

  // Keep sendOtp as alias for backward compat
  const sendOtp = sendMagicLink;

  // Verify the 6-digit code a user types from their email.
  // Bypasses email-prefetch/antivirus issues because the code never
  // travels the network until the user pastes it.
  const verifyOtpCode = async (email, code) => {
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token: code.trim(),
        type: 'email',
      });
      if (error) throw error;
      return data;
    } catch (error) {
      const msg = (error && error.message) || '';
      // Supabase-JS v2.98 throws "Failed to execute 'json' on 'Response':
      // body stream already read" when the verify endpoint returns a non-200.
      // 99% of the time this means the underlying token was already consumed
      // (e.g. Outlook Safe Links pre-clicked the magic link) or the code is
      // wrong/expired. Translate it into a human message.
      if (/body stream already read/i.test(msg)) {
        throw new Error(
          "That code didn't work — your inbox provider may have already used it, or the code is wrong. Request a new one and try again."
        );
      }
      if (/expired|invalid|not found|incorrect/i.test(msg)) {
        throw new Error("That code isn't right or has expired. Request a new one and try again.");
      }
      throw error;
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setToken(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      isAuthenticated: !!user,
      sendOtp,
      sendMagicLink,
      verifyOtpCode,
      logout,
      updateUser,
      // Legacy compat
      login: null,
      register: null,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
